import { useCallback, useMemo, useRef, useState, memo } from "react";
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Typography,
  Chip,
  Stack,
} from "@mui/material";
import { AccountBalance as CommunityIcon, Home as HomeIcon } from "@mui/icons-material";
import FilterGroup from "./advanced-filter-builder/FilterGroup";
import RecipientsPreview from "./advanced-filter-builder/RecipientsPreview";
import {
  ITS_COLUMNS,
  MUTTAVATTEEN_MEMBER_COLUMNS,
  type FilterGroupData,
  type ItsColumnDef,
} from "./advanced-filter-builder/constants";
import {
  createEmptyFilterGroup,
  parseMiqaatRsvpFilterEnvelopeForForm,
  type MiqaatRsvpFilterEnvelope,
} from "./miqaat-rsvp-filter-criteria";

type MiqaatRsvpAudienceFiltersProps = {
  value: unknown;
  onChange?: (envelope: MiqaatRsvpFilterEnvelope) => void;
  onPreviewCount?: (count: number) => void;
  onSelectionChange?: (
    recipients: { ITS_ID: string | null; phone: string; Full_Name: string | null }[]
  ) => void;
  onPreviewed?: (previewed: boolean) => void;
};

const AudienceSectionCard = memo(
  ({
    title,
    helper,
    icon,
    include,
    onIncludeChange,
    filter,
    onFilterChange,
    onFilterReset,
    columns,
  }: {
    title: string;
    helper: string;
    icon: React.ReactNode;
    include: boolean;
    onIncludeChange: (include: boolean) => void;
    filter: FilterGroupData;
    onFilterChange: (group: FilterGroupData) => void;
    onFilterReset: () => void;
    columns: ItsColumnDef[];
  }) => (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        borderColor: include ? "primary.light" : "divider",
        transition: "all 0.2s ease",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: include ? 1.5 : 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                p: 0.75,
                borderRadius: 1.5,
                bgcolor: include ? "primary.lighter" : "action.hover",
                color: include ? "primary.main" : "text.secondary",
                display: "flex",
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                {helper}
              </Typography>
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={include}
                onChange={(_, checked) => onIncludeChange(checked)}
                color="primary"
              />
            }
            label={
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: include ? "primary.main" : "text.secondary" }}
              >
                {include ? "Included" : "Excluded"}
              </Typography>
            }
            labelPlacement="start"
            sx={{ mr: 0 }}
          />
        </Box>

        {include ? (
          <FilterGroup
            group={filter}
            onUpdate={onFilterChange}
            onDelete={onFilterReset}
            level={0}
            availableColumns={columns}
          />
        ) : (
          <Box
            sx={{
              p: 1.25,
              mt: 1,
              borderRadius: 1.5,
              bgcolor: "action.hover",
              textAlign: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              This audience is excluded and will not receive RSVP messages.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
);

AudienceSectionCard.displayName = "AudienceSectionCard";

const MiqaatRsvpAudienceFilters = memo(
  ({
    value,
    onChange,
    onPreviewCount,
    onSelectionChange,
    onPreviewed,
  }: MiqaatRsvpAudienceFiltersProps) => {
    const envelope = useMemo(() => parseMiqaatRsvpFilterEnvelopeForForm(value), [value]);

    const skippedIdsMapRef = useRef(new Map<string, Set<string>>());
    const shouldFetchMapRef = useRef(new Map<string, boolean>());
    const [skippedIdsMap, setSkippedIdsMap] = useState(skippedIdsMapRef.current);
    const [shouldFetchMap, setShouldFetchMap] = useState(shouldFetchMapRef.current);

    const updateSkippedIdsMap = useCallback(
      (updater: (prev: Map<string, Set<string>>) => Map<string, Set<string>>) => {
        setSkippedIdsMap((prev) => {
          const next = updater(prev);
          skippedIdsMapRef.current = next;
          return next;
        });
      },
      []
    );

    const updateShouldFetchMap = useCallback(
      (updater: (prev: Map<string, boolean>) => Map<string, boolean>) => {
        setShouldFetchMap((prev) => {
          const next = updater(prev);
          shouldFetchMapRef.current = next;
          return next;
        });
      },
      []
    );

    const emit = useCallback(
      (next: MiqaatRsvpFilterEnvelope) => {
        onChange?.(next);
      },
      [onChange]
    );

    const itsFilter = envelope.itsDirectory.filter ?? createEmptyFilterGroup("its-root");
    const muttavatteenFilter =
      envelope.muttavatteen.filter ?? createEmptyFilterGroup("muttavatteen-root");

    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {/* Clean Header with Audience Summary Badges */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mb: 0.75,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
              Miqaat RSVP Audiences
            </Typography>

            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <Chip
                label="Auto De-duplication"
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 24, fontSize: "0.7rem", fontWeight: 600 }}
              />
              <Chip
                label="Misaaq Done & Mutawatteen"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 24, fontSize: "0.7rem", fontWeight: 600 }}
              />
            </Stack>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Configure targeting for ITS community members and/or Mutawatteen households. Leave rules
            empty to send to all active members.
          </Typography>
        </Box>

        {/* ITS Directory Card */}
        <AudienceSectionCard
          title="ITS Directory Audience"
          helper="Filters Misaaq Done community members from the ITS directory."
          icon={<CommunityIcon fontSize="small" />}
          include={envelope.itsDirectory.include}
          onIncludeChange={(include) =>
            emit({
              ...envelope,
              itsDirectory: { ...envelope.itsDirectory, include },
            })
          }
          filter={itsFilter}
          onFilterChange={(filter) =>
            emit({
              ...envelope,
              itsDirectory: { ...envelope.itsDirectory, filter },
            })
          }
          onFilterReset={() =>
            emit({
              ...envelope,
              itsDirectory: {
                ...envelope.itsDirectory,
                filter: createEmptyFilterGroup("its-root"),
              },
            })
          }
          columns={ITS_COLUMNS}
        />

        {/* Mutawatteen Card */}
        <AudienceSectionCard
          title="Mutawatteen Audience"
          helper="Filters Mutawatteen household members. Matches expand to all WhatsApp numbers in the household."
          icon={<HomeIcon fontSize="small" />}
          include={envelope.muttavatteen.include}
          onIncludeChange={(include) =>
            emit({
              ...envelope,
              muttavatteen: { ...envelope.muttavatteen, include },
            })
          }
          filter={muttavatteenFilter}
          onFilterChange={(filter) =>
            emit({
              ...envelope,
              muttavatteen: { ...envelope.muttavatteen, filter },
            })
          }
          onFilterReset={() =>
            emit({
              ...envelope,
              muttavatteen: {
                ...envelope.muttavatteen,
                filter: createEmptyFilterGroup("muttavatteen-root"),
              },
            })
          }
          columns={MUTTAVATTEEN_MEMBER_COLUMNS}
        />

        {/* Preview Card */}
        <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Union Preview & Verification
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
              Preview the phone-deduplicated union of all active audiences above
            </Typography>

            <RecipientsPreview
              filterGroup={envelope}
              onPreviewCount={onPreviewCount}
              onSelectionChange={onSelectionChange}
              onPreviewed={onPreviewed}
              skippedIdsMap={skippedIdsMap}
              setSkippedIdsMap={updateSkippedIdsMap}
              shouldFetchMap={shouldFetchMap}
              setShouldFetchMap={updateShouldFetchMap}
            />
          </CardContent>
        </Card>
      </Box>
    );
  }
);

MiqaatRsvpAudienceFilters.displayName = "MiqaatRsvpAudienceFilters";

export default MiqaatRsvpAudienceFilters;
